import { Check, Eye, EyeOff, X } from 'lucide-react';
import { forwardRef, useId, useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { FieldError, Flex, IconButton, Label, Text, TextField } from '@/components/common';
import { cn } from '@/lib/utils';

interface PasswordInputProps extends Omit<ComponentPropsWithoutRef<typeof TextField.Root>, 'type'> {
  label?: ReactNode;
  error?: string;
  helperText?: string;
  showStrengthCheck?: boolean;
  minLength?: number;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      error,
      helperText,
      className,
      id,
      name,
      value,
      defaultValue = '',
      onChange,
      onBlur,
      showStrengthCheck = true,
      minLength = 8,
      ...textFieldProps
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(false);
    const [uncontrolledValue, setUncontrolledValue] = useState(String(defaultValue));
    const isControlled = value !== undefined;
    const currentValue = String(isControlled ? value : uncontrolledValue);

    const generatedId = useId();
    const inputId = id ?? name ?? `password-input-${generatedId}`;

    const requirements = [
      {
        id: 'length',
        label: `At least ${minLength} characters`,
        valid: currentValue.length >= minLength,
      },
      { id: 'uppercase', label: 'One uppercase letter', valid: /[A-Z]/.test(currentValue) },
      { id: 'lowercase', label: 'One lowercase letter', valid: /[a-z]/.test(currentValue) },
      { id: 'number', label: 'One number', valid: /\d/.test(currentValue) },
      { id: 'special', label: 'One special character', valid: /[^A-Za-z0-9]/.test(currentValue) },
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setUncontrolledValue(e.target.value);
      }
      onChange?.(e);
    };

    return (
      <Flex direction="column" gap="2">
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <TextField.Root
          ref={ref}
          id={inputId}
          name={name}
          type={isVisible ? 'text' : 'password'}
          value={isControlled ? value : uncontrolledValue}
          onChange={handleChange}
          onBlur={onBlur}
          className={cn('w-full', className)}
          {...textFieldProps}
        >
          <TextField.Slot side="right" pr="1">
            <IconButton
              type="button"
              variant="ghost"
              size="1"
              color="gray"
              aria-label={isVisible ? 'Hide password' : 'Show password'}
              onClick={() => setIsVisible(prev => !prev)}
            >
              {isVisible ? <EyeOff size={16} /> : <Eye size={16} />}
            </IconButton>
          </TextField.Slot>
        </TextField.Root>
        {error && <FieldError message={error} />}
        {helperText && !error && (
          <Text size="1" color="gray">
            {helperText}
          </Text>
        )}
        {showStrengthCheck && currentValue.length > 0 && (
          <Flex asChild direction="column" gap="1">
            <ul aria-label="Password requirements" aria-live="polite">
              {requirements.map(req => (
                <Flex asChild align="center" gap="2" key={req.id}>
                  <li>
                    <Text as="span" color={req.valid ? 'green' : 'gray'} size="1">
                      {req.valid ? <Check size={12} /> : <X size={12} />}
                    </Text>
                    <Text size="1" color={req.valid ? 'green' : 'gray'}>
                      {req.label}
                    </Text>
                  </li>
                </Flex>
              ))}
            </ul>
          </Flex>
        )}
      </Flex>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };
export type { PasswordInputProps };
